import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import LoginPage from './pages/LoginPage';
import ValidatorPage from './pages/ValidatorPage';
import HistoryPage from './pages/HistoryPage';
import RegisterPage from './pages/RegisterPage';
import AdminUsers from './pages/AdminUsers';
import AdminUsersList from './pages/AdminUsersList';
import AdminTranslations from './pages/AdminTranslations';
import AdminValidatorGroups from './pages/AdminValidatorGroups';
import ProdigyAdminPage from './pages/ProdigyAdminPage';
import SupervisorPage from './pages/SupervisorPage';
import ProdigyPage from './pages/ProdigyPage';
import ReportPage from './pages/ReportPage';
import TranslationsReportPage from './pages/TranslationsReportPage';
import { validatorGroupsAPI } from './services/api';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!user?.is_superuser) return <Navigate to="/validator" replace />;

  return children;
};

const ReportRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!user?.is_superuser && !user?.can_view_report) return <Navigate to="/validator" replace />;

  return children;
};

// SupervisorRoute: only users that have at least one supervised student can access
const SupervisorRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    // Wait until auth has fully resolved before doing anything
    if (loading) return;
    if (!isAuthenticated) { setChecking(false); return; }
    if (user?.is_superuser) { setAllowed(true); setChecking(false); return; }
    setChecking(true);
    validatorGroupsAPI.mySupervised()
      .then(res => { setAllowed((res.data || []).length > 0); })
      .catch(() => { setAllowed(false); })
      .finally(() => setChecking(false));
  }, [isAuthenticated, loading, user]);

  if (loading || checking) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/validator" replace />;
  return children;
};

// App Component
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <ToastContainer position="top-right" autoClose={5000} />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route 
              path="/validator" 
              element={
                <ProtectedRoute>
                  <ValidatorPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/history" 
              element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/prodigy" 
              element={
                <ProtectedRoute>
                  <ProdigyPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsersList />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/pending"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/translations"
              element={
                <AdminRoute>
                  <AdminTranslations />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/validator-groups"
              element={
                <AdminRoute>
                  <AdminValidatorGroups />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/prodigy"
              element={
                <AdminRoute>
                  <ProdigyAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ReportRoute>
                  <ReportPage />
                </ReportRoute>
              }
            />
            <Route
              path="/report/translations"
              element={
                <ReportRoute>
                  <TranslationsReportPage />
                </ReportRoute>
              }
            />
            <Route
              path="/supervisor"
              element={
                <SupervisorRoute>
                  <SupervisorPage />
                </SupervisorRoute>
              }
            />
            <Route path="/" element={<Navigate to="/validator" replace />} />
            <Route path="*" element={<Navigate to="/validator" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;